import React, { createContext, useContext, useState } from 'react';
import useCream from '../cream';
import PickBetDialog from './pickBetDialog';
import NotEnoughCreamDialog from './notEnoughDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { IFightNft } from '../../interfaces/FightNft';

export enum FightPageModeMarkers {
  PICK,
  BATTLE,
  RESULTS
}

type ModeHome = { marker: FightPageModeMarkers.PICK };
type ModeFight = {
  marker: FightPageModeMarkers.BATTLE;
  nft: IFightNft;
  bet: number;
  straightToFight: boolean;
};
type ModeResults = {
  marker: FightPageModeMarkers.RESULTS;
  fightId: string;
  needCongrats: boolean;
};
export type FightPageMode = ModeHome | ModeFight | ModeResults;

interface PickDialogState {
  opened: boolean;
  notEnoughOpened: boolean;
  nft?: IFightNft;
}

interface IInitFight {
  openPickDialog: (nft: IFightNft, stopLoading: () => any) => void;
  mode: FightPageMode;
  goHome: () => void;
  goFight: (nft: IFightNft, bet: number, straightToFight: boolean) => void;
  goResults: (fightId: string, needCongrats: boolean) => void;
}

// @ts-ignore
const InitFightContext = createContext<IInitFight>();

interface Props {
  children: React.ReactNode;
}

export const InitFightProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getBalance, balance } = useCream();

  const [mode, setMode] = useState<FightPageMode>({
    marker: FightPageModeMarkers.PICK
  });

  const [pickDialogState, setPickDialogState] = useState<PickDialogState>({
    opened: false,
    notEnoughOpened: false,
    nft: undefined
  });

  const openPickDialog = (nft: IFightNft, stopLoading?: () => any) => {
    getBalance().then((result) => {
      if (result?.balance !== undefined && result?.balance !== null) {
        setPickDialogState({
          opened: true,
          notEnoughOpened: false,
          nft
        });
      }
      if (stopLoading) stopLoading();
    });
  };

  const goHome = () => {
    getBalance();
    setMode({ marker: FightPageModeMarkers.PICK });
  };

  const goFight = (
    nft: IFightNft,
    bet: number,
    straightToFight: boolean = false
  ) => {
    setMode({ marker: FightPageModeMarkers.BATTLE, nft, bet, straightToFight });
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const goResults = (fightId: string, needCongrats: boolean) => {
    getBalance();
    setMode({ marker: FightPageModeMarkers.RESULTS, fightId, needCongrats });
  };

  const closePick = () => {
    setPickDialogState((prev) => ({ ...prev, opened: false }));
  };

  const openNotEnough = () => {
    setPickDialogState((prev) => ({ ...prev, notEnoughOpened: true }));
  };

  const closeNotEnough = () => {
    setPickDialogState((prev) => ({ ...prev, notEnoughOpened: false }));
  };

  return (
    <InitFightContext.Provider
      value={{
        openPickDialog,
        mode,
        goHome,
        goFight,
        goResults
      }}
    >
      {children}
      <PickBetDialog
        nft={pickDialogState.nft}
        opened={pickDialogState.opened}
        onClose={closePick}
        openNotEnough={openNotEnough}
        balance={balance}
        onSelect={(nft, bet) => goFight(nft, bet)}
      />
      <NotEnoughCreamDialog
        opened={pickDialogState.notEnoughOpened}
        onClose={closeNotEnough}
      />
    </InitFightContext.Provider>
  );
};

const useInitFight = () => useContext(InitFightContext);

export default useInitFight;
